import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GAME_TYPE, GameSession } from "@/lib/api/games";
import { Zap, Brain, TrendingUp, Clock } from "lucide-react";

interface EndGameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEndGame: (score: number) => void;
  activeGame: GameSession | null;
  isLoading?: boolean;
}

const GAME_INFO = {
  [GAME_TYPE.TAP_COUNTER]: {
    label: "Tap Counter",
    icon: Zap,
    color: "text-emerald-600",
    bgColor: "bg-emerald-50",
    scoreLabel: "Taps",
    scorePlaceholder: "e.g., 42",
  },
  [GAME_TYPE.SEQUENCE_REPEATER]: {
    label: "Sequence Repeater",
    icon: Brain,
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    scoreLabel: "Score",
    scorePlaceholder: "e.g., 150",
  },
  [GAME_TYPE.ASCENT]: {
    label: "Ascent",
    icon: TrendingUp,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    scoreLabel: "Points",
    scorePlaceholder: "e.g., 250",
  },
};

export function EndGameDialog({
  open,
  onOpenChange,
  onEndGame,
  activeGame,
  isLoading,
}: EndGameDialogProps) {
  const [score, setScore] = useState("");

  const handleEnd = () => {
    const numericScore = parseInt(score, 10);
    if (!isNaN(numericScore) && numericScore >= 0) {
      onEndGame(numericScore);
      setScore("");
    }
  };

  const gameInfo = activeGame
    ? GAME_INFO[activeGame.game_type]
    : {
        label: "Game",
        icon: Clock,
        color: "text-gray-600",
        bgColor: "bg-gray-50",
        scoreLabel: "Score",
        scorePlaceholder: "e.g., 100",
      };

  const Icon = gameInfo.icon;

  const getElapsedTime = () => {
    if (!activeGame) return "0s";
    const elapsed = Math.floor(
      (Date.now() - new Date(activeGame.created_at).getTime()) / 1000,
    );
    if (elapsed < 60) return `${elapsed}s`;
    return `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>End Game</DialogTitle>
          <DialogDescription>
            Enter your final score to complete the game.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {activeGame && (
            <div
              className={`rounded-lg border p-4 ${gameInfo.bgColor} border-gray-200`}
            >
              <div className="mb-2 flex items-center gap-2">
                <Icon className={`h-5 w-5 ${gameInfo.color}`} />
                <h4 className="font-semibold">{gameInfo.label}</h4>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="h-4 w-4" />
                <span>Playing for {getElapsedTime()}</span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="score">{gameInfo.scoreLabel}</Label>
            <Input
              id="score"
              type="number"
              min="0"
              placeholder={gameInfo.scorePlaceholder}
              value={score}
              onChange={(e) => setScore(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && score) {
                  handleEnd();
                }
              }}
              autoFocus
            />
            <p className="text-xs text-gray-500">
              Enter a positive number for your final score
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEnd}
            disabled={!score || parseInt(score) < 0 || isLoading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? "Ending..." : "End Game"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
